import { getLocale, t, tn, useI18n } from '../../i18n';
import React, { useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiCalendar,
  FiChevronDown,
  FiChevronRight,
  FiDownload,
  FiEye,
  FiSave,
  FiX,
} from 'react-icons/fi';
import {
  aggregateRows,
  buildCsvForKind,
  buildPayrollFilename,
  getPayrollDateRange,
  PAYROLL_EXPORT_KINDS,
  PAYROLL_PERIOD_OPTIONS,
  payrollService,
  type ExportProfileKind,
  type PayrollExportResult,
  type PayrollGroupBy,
  type PayrollPeriodKey,
  type PayrollSettings,
} from '../../services/payrollService';
import PayCollapsible from '../payroll/PayCollapsible';
import './AdminPage.css';
import '../payroll/PayrollWorkspace.css';

const GROUP_OPTIONS: { key: PayrollGroupBy; label: string }[] = [
  { key: 'employee', label: 'Employee' },
  { key: 'site', label: 'Site' },
  { key: 'pay_band', label: 'Pay band' },
  { key: 'cost_centre', label: 'Cost centre' },
  { key: 'week', label: 'Week' },
  { key: 'employment_type', label: 'Employment type' },
];

interface AdminPayrollPageProps {
  /** Managers may run and export payroll but not change the rules behind it. */
  canEditSettings?: boolean;
}

function getTodayDateKey(): string {
  const now = new Date();
  return [
    String(now.getFullYear()),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function formatHours(value: number): string {
  return value.toLocaleString(getLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatMoney(value: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatGeneratedAt(value: string | null | undefined): string {
  if (!value) return t('Not generated yet');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t('Not generated yet');
  return date.toLocaleString(getLocale(), {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatDisplayPayrollRange(startDate: string | null, endDate: string | null): string {
  if (!startDate || !endDate) return t('All time');
  return t('{startDate} to {endDate}', { startDate, endDate });
}

function toNumber(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clampNumber(value: number, min: number, max?: number): number {
  const capped = max == null ? value : Math.min(value, max);
  return Math.max(capped, min);
}

function buildInputSignature(
  period: PayrollPeriodKey,
  startDate: string,
  endDate: string,
  settings: PayrollSettings | null,
): string {
  if (!settings) return '';
  const calculationSettings = Object.fromEntries(
    Object.entries(settings).filter(([key]) => !['companyId', 'updatedAt', 'source'].includes(key)),
  );
  return JSON.stringify({
    period,
    startDate: period === 'all_time' ? null : startDate,
    endDate: period === 'all_time' ? null : endDate,
    settings: calculationSettings,
  });
}

type NumericSettingKey = keyof Pick<
  PayrollSettings,
  | 'baseHourlyRateEur'
  | 'sundayPremiumMultiplier'
  | 'publicHolidayBenefitMultiplier'
  | 'unsocialHoursShiftPremiumEur'
  | 'unsocialHoursMinMinutes'
  | 'minimumShiftPayMinutes'
  | 'overtimeDailyThresholdMinutes'
  | 'overtimeDailyMultiplier'
  | 'overtimeWeeklyThresholdMinutes'
  | 'overtimeWeeklyMultiplier'
  | 'doubleTimeDailyThresholdMinutes'
  | 'doubleTimeMultiplier'
  | 'roundingIntervalMinutes'
  | 'clockInGraceMinutes'
  | 'clockOutGraceMinutes'
  | 'varianceFlagMinutes'
  | 'annualLeaveAccrualPercent'
>;

export default function AdminPayrollPage({ canEditSettings = true }: AdminPayrollPageProps) {
  useI18n();
  const today = useMemo(getTodayDateKey, []);
  const initialRange = useMemo(() => getPayrollDateRange('week', today), [today]);

  const [period, setPeriod] = useState<PayrollPeriodKey>('week');
  const [startDate, setStartDate] = useState(initialRange.startDate ?? '');
  const [endDate, setEndDate] = useState(initialRange.endDate ?? today);
  const [settings, setSettings] = useState<PayrollSettings | null>(null);
  const [draftSettings, setDraftSettings] = useState<PayrollSettings | null>(null);
  const [result, setResult] = useState<PayrollExportResult | null>(null);
  const [groupBy, setGroupBy] = useState<PayrollGroupBy>('employee');
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedInputSignature, setGeneratedInputSignature] = useState('');
  const [resultUsedUnsavedSettings, setResultUsedUnsavedSettings] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const currency = settings?.currencyCode ?? 'EUR';

  const dateRangeLabel = useMemo(
    () => formatDisplayPayrollRange(period === 'all_time' ? null : startDate, period === 'all_time' ? null : endDate),
    [period, startDate, endDate],
  );

  const settingsAreDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(draftSettings),
    [settings, draftSettings],
  );

  const aggregates = useMemo(
    () => (result ? aggregateRows(result.rows, groupBy) : []),
    [result, groupBy],
  );

  const currentInputSignature = useMemo(
    () => buildInputSignature(period, startDate, endDate, draftSettings),
    [period, startDate, endDate, draftSettings],
  );

  const resultIsStale = Boolean(result && generatedInputSignature !== currentInputSignature);
  const dateRangeError = period !== 'all_time' && (!startDate || !endDate)
    ? t('Choose both a start and end date.')
    : period !== 'all_time' && startDate > endDate
      ? t('Start date must be on or before the end date.')
      : '';

  const loadSettings = React.useCallback(async () => {
    setIsLoadingSettings(true);
    setError('');
    try {
      const nextSettings = await payrollService.fetchPayrollSettings();
      setSettings(nextSettings);
      setDraftSettings({ ...nextSettings });
    } catch (err) {
      setError((err as Error).message || t('Failed to load payroll settings'));
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  React.useEffect(() => { void loadSettings(); }, [loadSettings]);

  const applyPeriodRange = (nextPeriod: PayrollPeriodKey, anchorDate: string) => {
    const range = getPayrollDateRange(nextPeriod, anchorDate || today);
    setStartDate(range.startDate ?? '');
    setEndDate(range.endDate ?? (anchorDate || today));
  };

  const handlePeriodChange = (nextPeriod: PayrollPeriodKey) => {
    setPeriod(nextPeriod);
    applyPeriodRange(nextPeriod, endDate || today);
  };

  const updateDraftNumber = (
    key: NumericSettingKey,
    value: string,
    options: { min: number; max?: number; minutesFromHours?: boolean; percentFromWhole?: boolean },
  ) => {
    setDraftSettings((current) => {
      if (!current) return current;
      const parsed = clampNumber(toNumber(value, Number(current[key])), options.min, options.max);
      let next: number = parsed;
      if (options.minutesFromHours) next = Math.round(parsed * 60);
      if (options.percentFromWhole) next = parsed / 100;
      return { ...current, [key]: next };
    });
  };

  const saveSettings = async () => {
    if (!draftSettings) return;
    setIsSavingSettings(true);
    setError('');
    setMessage('');
    try {
      const saved = await payrollService.savePayrollSettings(draftSettings);
      setSettings(saved);
      setDraftSettings({ ...saved });
      if (generatedInputSignature === buildInputSignature(period, startDate, endDate, saved)) {
        setResultUsedUnsavedSettings(false);
      }
      setMessage(t('Payroll settings saved.'));
    } catch (err) {
      setError((err as Error).message || t('Failed to save payroll settings'));
    } finally {
      setIsSavingSettings(false);
    }
  };

  const generateExport = async () => {
    if (!draftSettings || dateRangeError) return;
    setIsGenerating(true);
    setError('');
    setMessage('');
    // Disable the previous result as soon as a refresh is attempted. If the
    // live query fails, users must not download figures that may no longer
    // match the underlying punches or assignments.
    if (result) setGeneratedInputSignature('');
    try {
      const exportResult = await payrollService.fetchPayrollExport({
        period,
        startDate: period === 'all_time' ? null : startDate,
        endDate: period === 'all_time' ? null : endDate,
        settingsOverride: draftSettings,
      });
      setResult(exportResult);
      setGeneratedInputSignature(currentInputSignature);
      setResultUsedUnsavedSettings(settingsAreDirty);
      setExpandedEmployeeId(null);
      setMessage(t('Gross-pay preview generated for {range}.', {
        range: formatDisplayPayrollRange(exportResult.startDate, exportResult.endDate),
      }));
    } catch (err) {
      setError((err as Error).message || t('Failed to generate gross-pay preview'));
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadCsv = (kind: ExportProfileKind) => {
    if (!result || isGenerating || resultIsStale || resultUsedUnsavedSettings) return;
    const csv = buildCsvForKind(result, kind);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildPayrollFilename(result, kind);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const rowsForEmployee = (employeeId: string) => (
    result?.rows.filter((row) => row.employeeId === employeeId) ?? []
  );

  return (
    <div className="pay-page">
      <div className="pay-page__header" data-tour="payroll-preview-header">
        <div className="pay-page__heading">
          <div className="admin-account-page__title-row">
            <h1 className="pay-page__title">{t('Gross pay preview')}</h1>
            <span className="admin-payroll-page__range-pill">
              <FiCalendar aria-hidden="true" />
              {dateRangeLabel}
            </span>
          </div>
          <p className="pay-page__subtitle">
            {t('Model gross pay directly from live clock records. Use Timesheets for the reviewed, locked register that becomes your payroll hand-off.')}
          </p>
        </div>

        <div className="pay-page__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => { void generateExport(); }}
            disabled={isGenerating || isLoadingSettings || !draftSettings || Boolean(dateRangeError)}
            data-tour="payroll-preview-generate"
          >
            <FiEye aria-hidden="true" />
            {isGenerating ? t('Generating preview...') : t('Preview gross pay')}
          </button>
        </div>
      </div>

      {(error || message) && (
        <div className="admin-account-page__messages">
          {error && <div className="msg--error">{error}</div>}
          {message && <div className="msg--success">{message}</div>}
        </div>
      )}

      <section className="pay-panel" data-tour="payroll-preview-period">
        <div className="pay-panel__head">
          <div style={{ flex: 1 }}>
            <h2>{t('Preview period')}</h2>
            <p>{t('Choose the roster window to model. This does not create or lock a pay period.')}</p>
          </div>
        </div>

        <div className="admin-payroll-page__period-grid">
          {PAYROLL_PERIOD_OPTIONS.filter((option) => option.key !== 'all_time').map((option) => (
            <button
              key={option.key}
              type="button"
              className={`admin-payroll-page__period ${period === option.key ? 'admin-payroll-page__period--active' : ''}`}
              onClick={() => handlePeriodChange(option.key)}
              aria-pressed={period === option.key}
            >
              {t(option.label)}
            </button>
          ))}
        </div>

        <div className="admin-payroll-page__date-grid">
          <label className="form__group">
            <span className="form__label">{t('Start date')}</span>
            <input
              type="date"
              className="form__input"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              disabled={period === 'all_time'}
            />
          </label>
          <label className="form__group">
            <span className="form__label">{t('End date')}</span>
            <input
              type="date"
              className="form__input"
              value={endDate}
              onChange={(event) => { setEndDate(event.target.value); applyPeriodRange(period, event.target.value || today); }}
              disabled={period === 'all_time'}
            />
          </label>
        </div>
        {dateRangeError && <div className="pay-field-error" role="alert">{dateRangeError}</div>}
      </section>

      {canEditSettings && draftSettings && (
        <PayCollapsible
          title={t('Payroll rules')}
          description={t('Defaults follow current Irish security-sector payroll rules. Pay bands can override the premium values per grade.')}
          meta={settingsAreDirty ? t('Unsaved payroll settings') : undefined}
          storageKey="odinseye.payroll.rulesOpen"
          tourTarget="payroll-preview-rules"
        >
          <div className="admin-payroll-page__legal-note">
            {t('Irish shifts are protected by effective-dated Security ERO floors: EUR 15.41 from 22 July 2025 and EUR 16.15 from 28 September 2026, plus the EUR 20 unsocial-hours shift premium, 21:00-07:00 window, 3-hour threshold, and 4-hour minimum shift pay. Lower company or pay-band values cannot reduce those Irish entitlements. Sunday premium defaults to a 25% top-up starting assumption because Irish law requires a Sunday benefit where it is not already included, but does not set one universal multiplier.')}
          </div>

          <div className="pay-settings-group">
          <h3 className="pay-drill__title">{t('Base and premiums')}</h3>
          <div className="pay-form-grid">
            <label className="form__group">
              <span className="form__label">{t('Base hourly rate (Security ERO)')}</span>
              <input type="number" className="form__input" min="0" step="0.01" value={draftSettings.baseHourlyRateEur}
                onChange={(event) => updateDraftNumber('baseHourlyRateEur', event.target.value, { min: 0 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Sunday premium top-up multiplier')}</span>
              <input type="number" className="form__input" min="0" step="0.01" value={draftSettings.sundayPremiumMultiplier}
                onChange={(event) => updateDraftNumber('sundayPremiumMultiplier', event.target.value, { min: 0 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Public holiday additional pay multiplier')}</span>
              <input type="number" className="form__input" min="0" step="0.01" value={draftSettings.publicHolidayBenefitMultiplier}
                onChange={(event) => updateDraftNumber('publicHolidayBenefitMultiplier', event.target.value, { min: 0 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Unsocial premium per shift')}</span>
              <input type="number" className="form__input" min="0" step="0.01" value={draftSettings.unsocialHoursShiftPremiumEur}
                disabled={draftSettings.useSecurityEroUnsocialRates}
                onChange={(event) => updateDraftNumber('unsocialHoursShiftPremiumEur', event.target.value, { min: 0 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Unsocial threshold (hours)')}</span>
              <input type="number" className="form__input" min="0" step="0.25" value={draftSettings.unsocialHoursMinMinutes / 60}
                onChange={(event) => updateDraftNumber('unsocialHoursMinMinutes', event.target.value, { min: 0, minutesFromHours: true })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Minimum shift pay (hours)')}</span>
              <input type="number" className="form__input" min="0" step="0.25" value={draftSettings.minimumShiftPayMinutes / 60}
                disabled={!draftSettings.applyMinimumShiftPay}
                onChange={(event) => updateDraftNumber('minimumShiftPayMinutes', event.target.value, { min: 0, minutesFromHours: true })} />
            </label>
          </div>

          </div>

          <div className="pay-settings-group">
          <h3 className="pay-drill__title">{t('Overtime')}</h3>
          <div className="admin-payroll-page__legal-note">
            {t('Overtime is off by default. The Security ERO bases overtime on average hours across the roster cycle, so validate these configurable thresholds against the applicable roster and employment terms before use.')}
          </div>
          <div className="pay-form-grid">
            <label className="form__group">
              <span className="form__label">{t('Daily overtime after (hours)')}</span>
              <input type="number" className="form__input" min="0" step="0.5" value={draftSettings.overtimeDailyThresholdMinutes / 60}
                disabled={!draftSettings.overtimeEnabled}
                onChange={(event) => updateDraftNumber('overtimeDailyThresholdMinutes', event.target.value, { min: 0, minutesFromHours: true })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Overtime multiplier')}</span>
              <input type="number" className="form__input" min="0" step="0.05" value={draftSettings.overtimeDailyMultiplier}
                disabled={!draftSettings.overtimeEnabled}
                onChange={(event) => updateDraftNumber('overtimeDailyMultiplier', event.target.value, { min: 0 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Weekly overtime after (hours)')}</span>
              <input type="number" className="form__input" min="0" step="1" value={draftSettings.overtimeWeeklyThresholdMinutes / 60}
                disabled={!draftSettings.overtimeEnabled}
                onChange={(event) => updateDraftNumber('overtimeWeeklyThresholdMinutes', event.target.value, { min: 0, minutesFromHours: true })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Weekly overtime multiplier')}</span>
              <input type="number" className="form__input" min="0" step="0.05" value={draftSettings.overtimeWeeklyMultiplier}
                disabled={!draftSettings.overtimeEnabled}
                onChange={(event) => updateDraftNumber('overtimeWeeklyMultiplier', event.target.value, { min: 0 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Overtime week starts')}</span>
              <select
                className="form__input"
                value={draftSettings.overtimeWeekStartWeekday}
                disabled={!draftSettings.overtimeEnabled}
                onChange={(event) => setDraftSettings((current) => current && {
                  ...current,
                  overtimeWeekStartWeekday: Number(event.target.value),
                })}
              >
                <option value={1}>{t('Monday')}</option>
                <option value={2}>{t('Tuesday')}</option>
                <option value={3}>{t('Wednesday')}</option>
                <option value={4}>{t('Thursday')}</option>
                <option value={5}>{t('Friday')}</option>
                <option value={6}>{t('Saturday')}</option>
                <option value={0}>{t('Sunday')}</option>
              </select>
            </label>
            <label className="form__group">
              <span className="form__label">{t('Double time after (hours)')}</span>
              <input type="number" className="form__input" min="0" step="0.5" value={draftSettings.doubleTimeDailyThresholdMinutes / 60}
                disabled={!draftSettings.doubleTimeEnabled}
                onChange={(event) => updateDraftNumber('doubleTimeDailyThresholdMinutes', event.target.value, { min: 0, minutesFromHours: true })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Double time multiplier')}</span>
              <input type="number" className="form__input" min="0" step="0.05" value={draftSettings.doubleTimeMultiplier}
                disabled={!draftSettings.doubleTimeEnabled}
                onChange={(event) => updateDraftNumber('doubleTimeMultiplier', event.target.value, { min: 0 })} />
            </label>
          </div>

          </div>

          <div className="pay-settings-group">
          <h3 className="pay-drill__title">{t('Rounding and tolerance')}</h3>
          <div className="pay-form-grid">
            <label className="form__group">
              <span className="form__label">{t('Rounding')}</span>
              <select className="form__input" value={draftSettings.roundingStrategy}
                onChange={(event) => setDraftSettings((current) => current && {
                  ...current, roundingStrategy: event.target.value as PayrollSettings['roundingStrategy'],
                })}>
                <option value="none">{t('No rounding')}</option>
                <option value="nearest">{t('Nearest')}</option>
                <option value="up">{t('Round up')}</option>
                <option value="down">{t('Round down')}</option>
              </select>
            </label>
            <label className="form__group">
              <span className="form__label">{t('Rounding interval (minutes)')}</span>
              <input type="number" className="form__input" min="1" max="60" step="1" value={draftSettings.roundingIntervalMinutes}
                disabled={draftSettings.roundingStrategy === 'none'}
                onChange={(event) => updateDraftNumber('roundingIntervalMinutes', event.target.value, { min: 1, max: 60 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Clock-in grace (minutes)')}</span>
              <input type="number" className="form__input" min="0" max="240" step="1" value={draftSettings.clockInGraceMinutes}
                onChange={(event) => updateDraftNumber('clockInGraceMinutes', event.target.value, { min: 0, max: 240 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Clock-out grace (minutes)')}</span>
              <input type="number" className="form__input" min="0" max="240" step="1" value={draftSettings.clockOutGraceMinutes}
                onChange={(event) => updateDraftNumber('clockOutGraceMinutes', event.target.value, { min: 0, max: 240 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Flag variance over (minutes)')}</span>
              <input type="number" className="form__input" min="0" step="5" value={draftSettings.varianceFlagMinutes}
                onChange={(event) => updateDraftNumber('varianceFlagMinutes', event.target.value, { min: 0 })} />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Annual leave accrual (%)')}</span>
              <input type="number" className="form__input" min="0" step="0.1" value={(draftSettings.annualLeaveAccrualPercent * 100).toFixed(1)}
                onChange={(event) => updateDraftNumber('annualLeaveAccrualPercent', event.target.value, { min: 0, percentFromWhole: true })} />
            </label>
          </div>

          <div className="admin-payroll-page__toggles">
            {([
              ['useSecurityEroUnsocialRates', 'Use security ERO unsocial-hours rate schedule'],
              ['applyMinimumShiftPay', 'Apply four-hour minimum shift pay rule'],
              ['overtimeEnabled', 'Enable overtime'],
              ['doubleTimeEnabled', 'Enable double time'],
              ['snapToSchedule', 'Snap clock times within grace to the schedule'],
              ['trackLeaveAccrual', 'Track annual leave accrual'],
            ] as [keyof PayrollSettings, string][]).map(([key, label]) => (
              <label key={key} className="admin-payroll-page__toggle">
                <input
                  type="checkbox"
                  checked={Boolean(draftSettings[key])}
                  onChange={(event) => setDraftSettings((current) => current && { ...current, [key]: event.target.checked })}
                />
                <span>{t(label)}</span>
              </label>
            ))}
          </div>

          </div>

          <div className="admin-payroll-page__settings-actions">
            <span className={settingsAreDirty ? 'admin-payroll-page__dirty' : 'admin-payroll-page__saved'}>
              {settingsAreDirty ? t('Unsaved payroll settings') : t('Payroll settings up to date')}
            </span>
            <div className="pay-inline-actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setDraftSettings(settings ? { ...settings } : null)}
                disabled={isSavingSettings || !settingsAreDirty}
              >
                <FiX aria-hidden="true" />
                {t('Discard changes')}
              </button>
              <button type="button" className="btn btn--secondary" onClick={() => { void saveSettings(); }} disabled={isSavingSettings || !settingsAreDirty}>
                <FiSave aria-hidden="true" />
                {isSavingSettings ? t('Saving...') : t('Save settings')}
              </button>
            </div>
          </div>
        </PayCollapsible>
      )}

      {result && (
        <>
          <section className={`pay-preview-notice ${resultIsStale ? 'pay-preview-notice--stale' : ''}`} role="status">
            {resultIsStale ? <FiAlertTriangle aria-hidden="true" /> : <FiEye aria-hidden="true" />}
            <div>
              <strong>
                {resultIsStale ? t('Preview out of date') : t('Live gross-pay preview — not locked')}
              </strong>
              <p>
                {resultIsStale
                  ? t('The dates or payroll rules changed after this preview was generated. Generate it again before downloading.')
                  : resultUsedUnsavedSettings
                    ? t('This preview uses unsaved payroll rules. Save the rules before downloading a CSV.')
                    : t('These figures come from current shift records and can still change. Final exports belong to a reviewed and locked Timesheets period.')}
              </p>
            </div>
          </section>

          {result.warnings.length > 0 && (
            <section className="pay-warnings" aria-label={t('Payroll review warnings')}>
              <FiAlertTriangle aria-hidden="true" />
              <div>
                {result.warnings.map((warning) => <p key={warning}>{warning}</p>)}
              </div>
            </section>
          )}

          {/* The estimate and the exception count are what the preview is for;
              the premium-hour breakdowns support them. */}
          <div className="pay-stats">
            <div className="pay-stat pay-stat--money pay-stat--lead">
              <span>{t('Estimated total')}</span>
              <strong>{formatMoney(result.totals.estimatedTotalPay, currency)}</strong>
            </div>
            <div className="pay-stat pay-stat--warn">
              <span>{t('Lines to review')}</span>
              <strong>{result.totals.needsReviewCount}</strong>
            </div>
            <div className="pay-stat">
              <span>{t('Payable hours')}</span>
              <strong>{formatHours(result.totals.payableHours)}</strong>
            </div>
            <div className="pay-stat">
              <span>{t('Overtime hours')}</span>
              <strong>{formatHours(result.totals.overtimeHours + result.totals.doubleTimeHours)}</strong>
            </div>
            <div className="pay-stat">
              <span>{t('Sunday hours')}</span>
              <strong>{formatHours(result.totals.sundayHours)}</strong>
            </div>
            <div className="pay-stat">
              <span>{t('Public holiday hours')}</span>
              <strong>{formatHours(result.totals.publicHolidayHours)}</strong>
            </div>
            <div className="pay-stat">
              <span>{t('Unsocial shifts')}</span>
              <strong>{result.totals.unsocialShiftCount}</strong>
            </div>
          </div>

          <section className="pay-panel">
            <div className="pay-panel__head">
              <div style={{ flex: 1 }}>
                <h2>{t('Preview employee summary')}</h2>
                <p>
                  {t('{count} employees included. Generated {date}.', {
                    count: result.summaries.length,
                    date: formatGeneratedAt(result.generatedAt),
                  })}
                </p>
              </div>
              <div className="pay-inline-actions">
                {PAYROLL_EXPORT_KINDS.filter((option) => option.kind !== 'timesheet_register').map((option) => (
                  <button
                    key={option.kind}
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => downloadCsv(option.kind)}
                    disabled={isGenerating || result.rows.length === 0 || resultIsStale || resultUsedUnsavedSettings}
                    title={t(option.description)}
                  >
                    <FiDownload aria-hidden="true" />
                    {t(option.label)}
                  </button>
                ))}
              </div>
            </div>

            {result.summaries.length === 0 ? (
              <div className="pay-empty">{t('No started shifts found for this period.')}</div>
            ) : (
              <div className="pay-table-wrap">
                <table className="pay-table">
                  <thead>
                    <tr>
                      <th>{t('Employee')}</th>
                      <th>{t('Band')}</th>
                      <th className="pay-table__num">{t('Rate')}</th>
                      <th className="pay-table__num">{t('Shifts')}</th>
                      <th className="pay-table__num">{t('Ordinary')}</th>
                      <th className="pay-table__num">{t('Overtime')}</th>
                      <th className="pay-table__num">{t('Sunday')}</th>
                      <th className="pay-table__num">{t('Public holiday')}</th>
                      <th className="pay-table__num">{t('Unsocial')}</th>
                      <th className="pay-table__money">{t('Premiums')}</th>
                      <th className="pay-table__money">{t('Total')}</th>
                      <th className="pay-table__num">{t('Review')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.summaries.map((summary) => (
                      <React.Fragment key={summary.employeeId}>
                        <tr>
                          <td>
                            <button
                              type="button"
                              className="pay-table__row-button"
                              onClick={() => setExpandedEmployeeId(
                                expandedEmployeeId === summary.employeeId ? null : summary.employeeId,
                              )}
                            >
                              {expandedEmployeeId === summary.employeeId
                                ? <FiChevronDown aria-hidden="true" />
                                : <FiChevronRight aria-hidden="true" />}
                              {summary.employeeName}
                            </button>
                          </td>
                          <td>
                            <span className={`pay-chip ${summary.payBandCode === 'UNBANDED' ? 'pay-chip--warn' : 'pay-chip--band'}`}>
                              {summary.payBandCode}
                            </span>
                          </td>
                          <td className="pay-table__money">
                            {summary.hourlyRate === null
                              ? t('Mixed rates')
                              : formatMoney(summary.hourlyRate, currency)}
                          </td>
                          <td className="pay-table__num">{summary.completeShiftCount}/{summary.shiftCount}</td>
                          <td className="pay-table__num">{formatHours(summary.ordinaryHours)}</td>
                          <td className="pay-table__num">
                            {formatHours(summary.overtimeHours + summary.doubleTimeHours)}
                            {summary.overtimeHours + summary.doubleTimeHours > 0 && (
                              <span className="pay-chip pay-chip--ot" style={{ marginLeft: 6 }}>{t('OT')}</span>
                            )}
                          </td>
                          <td className="pay-table__num">{formatHours(summary.sundayHours)}</td>
                          <td className="pay-table__num">{formatHours(summary.publicHolidayHours)}</td>
                          <td className="pay-table__num">{formatHours(summary.unsocialHours)}</td>
                          <td className="pay-table__money">
                            {formatMoney(
                              summary.sundayPremiumPay + summary.publicHolidayPremiumPay + summary.unsocialPremiumPay,
                              currency,
                            )}
                          </td>
                          <td className="pay-table__money">{formatMoney(summary.estimatedTotalPay, currency)}</td>
                          <td className="pay-table__num">{summary.needsReviewCount || '-'}</td>
                        </tr>

                        {expandedEmployeeId === summary.employeeId && (
                          <tr className="pay-drill">
                            <td colSpan={12}>
                              <div className="pay-drill__inner">
                                <p className="pay-drill__title">{t('Shift detail')}</p>
                                <div className="pay-table-wrap">
                                  <table className="pay-table">
                                    <thead>
                                      <tr>
                                        <th>{t('Date')}</th>
                                        <th>{t('Site')}</th>
                                        <th>{t('Clock in')}</th>
                                        <th>{t('Clock out')}</th>
                                        <th className="pay-table__num">{t('Gross')}</th>
                                        <th className="pay-table__num">{t('Break')}</th>
                                        <th className="pay-table__num">{t('Rounding')}</th>
                                        <th className="pay-table__num">{t('Ordinary')}</th>
                                        <th className="pay-table__num">{t('Overtime')}</th>
                                        <th className="pay-table__num">{t('Variance')}</th>
                                        <th className="pay-table__money">{t('Total')}</th>
                                        <th>{t('Flags')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {rowsForEmployee(summary.employeeId).map((row) => (
                                        <tr key={row.shiftExecutionId}>
                                          <td>{row.rosterDate}</td>
                                          <td className="pay-table__muted">{row.siteName}</td>
                                          <td className="pay-table__muted">{row.localClockIn || '-'}</td>
                                          <td className="pay-table__muted">{row.localClockOut || '-'}</td>
                                          <td className="pay-table__num">{formatHours(row.grossHours)}</td>
                                          <td className="pay-table__num">{formatHours(row.unpaidBreakHours)}</td>
                                          <td className="pay-table__num">
                                            {row.roundingAdjustmentHours === 0
                                              ? '-'
                                              : `${row.roundingAdjustmentHours > 0 ? '+' : ''}${formatHours(row.roundingAdjustmentHours)}`}
                                          </td>
                                          <td className="pay-table__num">{formatHours(row.ordinaryHours)}</td>
                                          <td className="pay-table__num">{formatHours(row.overtimeHours + row.doubleTimeHours)}</td>
                                          <td className="pay-table__num">
                                            {row.varianceHours === 0
                                              ? '-'
                                              : `${row.varianceHours > 0 ? '+' : ''}${formatHours(row.varianceHours)}`}
                                          </td>
                                          <td className="pay-table__money">{formatMoney(row.estimatedTotalPay, currency)}</td>
                                          <td>
                                            <div className="pay-flags">
                                              {row.flags.map((flag) => (
                                                <span key={flag} className="pay-chip pay-chip--warn">{t(flag)}</span>
                                              ))}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <PayCollapsible
            title={t('Aggregate breakdown')}
            description={t('The same run grouped a different way, for cost allocation and reporting.')}
            meta={tn('{count} group', '{count} groups', aggregates.length)}
            storageKey="odinseye.payroll.previewAggregateOpen"
          >
            <div className="pay-segment" style={{ marginBottom: 12 }}>
              {GROUP_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`pay-segment__item ${groupBy === option.key ? 'pay-segment__item--active' : ''}`}
                  onClick={() => setGroupBy(option.key)}
                >
                  {t(option.label)}
                </button>
              ))}
            </div>

            {aggregates.length === 0 ? (
              <div className="pay-empty">{t('Nothing to aggregate yet.')}</div>
            ) : (
              <div className="pay-table-wrap">
                <table className="pay-table">
                  <thead>
                    <tr>
                      <th>{t('Group')}</th>
                      <th className="pay-table__num">{t('Employees')}</th>
                      <th className="pay-table__num">{t('Shifts')}</th>
                      <th className="pay-table__num">{t('Payable')}</th>
                      <th className="pay-table__num">{t('Overtime')}</th>
                      <th className="pay-table__num">{t('Unsocial')}</th>
                      <th className="pay-table__money">{t('Ordinary pay')}</th>
                      <th className="pay-table__money">{t('Premiums')}</th>
                      <th className="pay-table__money">{t('Total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregates.map((row) => (
                      <tr key={row.groupKey}>
                        <td>{row.groupLabel}</td>
                        <td className="pay-table__num">{row.employeeCount}</td>
                        <td className="pay-table__num">{row.shiftCount}</td>
                        <td className="pay-table__num">{formatHours(row.payableHours)}</td>
                        <td className="pay-table__num">{formatHours(row.overtimeHours + row.doubleTimeHours)}</td>
                        <td className="pay-table__num">{formatHours(row.unsocialHours)}</td>
                        <td className="pay-table__money">{formatMoney(row.ordinaryPay, currency)}</td>
                        <td className="pay-table__money">{formatMoney(row.premiumPay, currency)}</td>
                        <td className="pay-table__money">{formatMoney(row.totalPay, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PayCollapsible>
        </>
      )}
    </div>
  );
}
